// The client half of the realtime wire envelope (ADR-0013). The server half is
// `packages/realtime/src/frame.ts`, and the two must agree exactly:
//
//   {"kind":"turbo-stream","html":"<turbo-stream …>"}  → <turbo-mercure-stream-source>
//   {"kind":"data","payload":{…}}                      → <mercure-data-source>
//
// WHY THIS IS NOT ZOD, GIVEN §12. §12 names four boundaries with no exceptions —
// inbound requests, third-party responses, queue payloads, and D1 JSON columns.
// A relayed frame is none of them, and it is the one input whose shape has
// ALREADY been validated with Zod, by `realtimeFrameSchema` in the Durable
// Object, before it went on the wire. Re-validating here would import Zod into
// every page's bundle to re-check something the sender proved. What this module
// does instead is narrow `unknown` honestly: the parse returns `null` rather
// than a lie, and no cast appears anywhere in it (§11).
//
// The narrowing is still real work, because JSON.parse returns `any`-shaped
// garbage for a malformed or hostile frame and the elements must not crash on
// one.

/** A Turbo Stream fragment, relayed verbatim and handed to Turbo. */
export interface TurboStreamFrame {
  readonly kind: "turbo-stream";
  readonly html: string;
}

/** A JSON data frame, re-dispatched as a `mercure:data` CustomEvent. */
export interface DataFrame {
  readonly kind: "data";
  readonly payload: unknown;
}

/** One relayed frame — discriminated on `kind` (§11). */
export type RealtimeFrame = TurboStreamFrame | DataFrame;

/** `typeof x === "object"` is true for `null`; this is the check that isn't. */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/**
 * Parse one wire message into a frame, or `null` if it is not one.
 *
 * `null` covers every rejection — non-string data (a binary frame), invalid
 * JSON, a missing or unknown `kind`, a `turbo-stream` without string `html`,
 * and a `data` whose payload is absent. Callers then IGNORE frames of the
 * other kind, because both kinds ride the same socket and each element handles
 * exactly one.
 *
 * A `data` frame's `payload` stays `unknown`: its shape is genuinely open (it
 * is `{_model,_id,_action,data}` today and whatever a producer sends tomorrow),
 * and the server deliberately does not constrain it either.
 */
export function parseFrame(data: unknown): RealtimeFrame | null {
  if (typeof data !== "string" || data === "") return null;

  let decoded: unknown;
  try {
    decoded = JSON.parse(data);
  } catch {
    return null;
  }
  if (!isRecord(decoded)) return null;

  if (decoded.kind === "turbo-stream") {
    const { html } = decoded;
    return typeof html === "string" ? { kind: "turbo-stream", html } : null;
  }
  if (decoded.kind === "data") {
    // `undefined` is unrepresentable in JSON, so absent and explicitly-undefined
    // are the same case — matching the server's own refinement. `null` is a
    // payload the old element dropped, and that behaviour is preserved.
    const { payload } = decoded;
    return payload === undefined || payload === null ? null : { kind: "data", payload };
  }
  return null;
}
